<template>
  <div ref="stageRef" class="stage">
    <div class="blobs" aria-hidden="true" :style="blobStyle">
      <span class="blob b1"></span>
      <span class="blob b2"></span>
      <span class="blob b3"></span>
    </div>
    <div class="grain" aria-hidden="true"></div>

    <div class="stage-inner">
      <section class="hero">
        <div class="hero-copy">
          <p class="kicker-chip">Rainbow Manager</p>
          <h1>{{ t('welcomeText') }}</h1>
          <p class="lead">{{ t('welcomeSubText') }}</p>
          <router-link class="cta" to="/signUp">{{ t('getStarted') }}</router-link>
        </div>

        <div class="hero-collage" :aria-label="t('homeLooksTitle')">
          <figure v-for="(card, index) in collage" :key="card.src" :class="'c' + index">
            <img :src="card.src" :alt="t(card.titleKey)" />
          </figure>
          <span class="season-badge">{{ t('softAutumnPaletteShort') }}</span>
        </div>
      </section>

      <home-audience />
      <home-looks />
      <home-season-preview />
      <home-frames-carousel />
      <home-palettes-grid />
      <home-studio-tools />

      <section v-home-reveal class="recolor" :aria-label="t('homeAiRecolorTitle')">
        <div class="recolor-copy">
          <p class="kicker-chip">{{ t('aiAgent') }}</p>
          <h2>{{ t('homeAiRecolorTitle') }}</h2>
          <p>{{ t('homeAiRecolorText') }}</p>
          <p>{{ t('homeFeatureAiText') }}</p>
          <router-link class="cta" to="/signUp">{{ t('getStarted') }}</router-link>
        </div>
        <div class="recolor-pair">
          <figure v-for="shot in recolor" :key="shot.src" :class="{ tall: shot.tall }">
            <img :src="shot.src" :alt="t(shot.captionKey)" />
            <figcaption>{{ t(shot.captionKey) }}</figcaption>
          </figure>
        </div>
      </section>

      <home-features />
      <home-stock-looks />
      <home-footer />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { HOME_HERO_COLLAGE, HOME_RECOLOR } from './home.data';
import { HomeLook } from './home.types';
import { vHomeReveal } from './home-reveal';
import HomeFramesCarousel from './HomeFramesCarousel.vue';
import HomePalettesGrid from './HomePalettesGrid.vue';
import HomeSeasonPreview from './HomeSeasonPreview.vue';
import HomeStudioTools from './HomeStudioTools.vue';
import HomeFeatures from './HomeFeatures.vue';
import HomeStockLooks from './HomeStockLooks.vue';
import HomeAudience from './HomeAudience.vue';
import HomeLooks from './HomeLooks.vue';
import HomeFooter from './HomeFooter.vue';

export default defineComponent({
  components: {
    HomeFramesCarousel,
    HomePalettesGrid,
    HomeSeasonPreview,
    HomeStudioTools,
    HomeFeatures,
    HomeStockLooks,
    HomeAudience,
    HomeLooks,
    HomeFooter,
  },
  directives: {
    homeReveal: vHomeReveal,
  },
  setup() {
    const { t } = useI18n();
    const stageRef = ref<HTMLElement | null>(null);
    const blobShift = ref({ x: 0, y: 0 });
    const canParallax = ref(false);
    const collage: HomeLook[] = HOME_HERO_COLLAGE;
    const recolor = HOME_RECOLOR;
    const blobStyle = computed(() => ({
      transform: `translate3d(${blobShift.value.x}px, ${blobShift.value.y}px, 0)`,
    }));

    const onPointerMove = (event: PointerEvent) => {
      if (!canParallax.value) return;
      const rect = stageRef.value?.getBoundingClientRect();
      if (!rect) return;
      const nx = (event.clientX - rect.left) / rect.width - 0.5;
      const ny = (event.clientY - rect.top) / rect.height - 0.5;
      blobShift.value = { x: nx * 28, y: ny * 18 };
    };

    onMounted(() => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      canParallax.value = !reduceMotion && finePointer;
      if (canParallax.value) {
        stageRef.value?.addEventListener('pointermove', onPointerMove);
      }
    });

    onBeforeUnmount(() => {
      stageRef.value?.removeEventListener('pointermove', onPointerMove);
    });

    return {
      t,
      stageRef,
      collage,
      recolor,
      blobStyle,
    };
  },
});
</script>
<style scoped lang="scss">
.stage {
  --home-bg: #141018;
  --home-ink: #f6f3ee;
  --home-pink: #fa496e;
  --home-violet: #a67eff;
  --home-cyan: #52dbff;
  --home-line: rgba(246, 243, 238, 0.16);
  --home-muted: rgba(246, 243, 238, 0.76);
  --home-soft: rgba(246, 243, 238, 0.58);
  --home-glass: rgba(20, 16, 24, 0.44);
  --home-glass-strong: rgba(20, 16, 24, 0.62);
  position: relative;
  isolation: isolate;
  min-height: 100%;
  overflow: hidden;
  padding: 8px clamp(20px, 5vw, 64px) 40px;
  color: var(--home-ink);
  background: var(--home-bg);
}

.blobs,
.grain {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.blobs {
  transition: transform 0.45s ease-out;
}

.grain {
  z-index: 1;
  opacity: 0.04;
  mix-blend-mode: overlay;
  background-image: radial-gradient(rgba(246, 243, 238, 0.55) 0.6px, transparent 0.7px);
  background-size: 3px 3px;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px) saturate(1.2);
  mix-blend-mode: screen;
  animation: blob-drift 18s ease-in-out infinite;
}

.b1 {
  width: 42vw;
  height: 36vw;
  left: -12%;
  top: -16%;
  background: var(--home-pink);
  opacity: 0.55;
}

.b2 {
  width: 38vw;
  height: 34vw;
  right: -10%;
  top: -8%;
  background: var(--home-violet);
  opacity: 0.5;
  animation-duration: 22s;
  animation-direction: reverse;
}

.b3 {
  width: 32vw;
  height: 28vw;
  right: 8%;
  top: 28%;
  background: var(--home-cyan);
  opacity: 0.38;
  animation-duration: 19s;
}

.stage-inner {
  position: relative;
  z-index: 2;
  width: min(1120px, 100%);
  margin: 0 auto;
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(280px, 1.08fr);
  gap: clamp(24px, 5vw, 64px);
  align-items: center;
  min-height: min(78vh, 680px);
  margin: 0 0 28px;
  padding: 28px 0 12px;
}

.hero-copy {
  text-align: left;
}

.hero-copy .kicker-chip,
.hero-copy h1,
.hero-copy .lead,
.hero-copy .cta {
  animation: rise-in 0.85s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.hero-copy h1 {
  animation-delay: 0.08s;
}

.hero-copy .lead {
  animation-delay: 0.16s;
}

.hero-copy .cta {
  animation-delay: 0.24s;
}

.kicker-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 18px;
  padding: 6px 12px 6px 10px;
  border-radius: 999px;
  border: 1px solid rgba(250, 73, 110, 0.38);
  background: rgba(250, 73, 110, 0.12);
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--home-ink);
}

.kicker-chip::before {
  content: '';
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--home-pink);
  box-shadow: 0 0 10px rgba(250, 73, 110, 0.8);
}

h1 {
  margin: 0 0 16px;
  max-width: 12ch;
  font-size: clamp(2.5rem, 6vw, 4.6rem);
  font-weight: 600;
  line-height: 0.96;
  letter-spacing: -0.055em;
}

.lead {
  margin: 0 0 28px;
  max-width: 38ch;
  font-size: 1.05rem;
  line-height: 1.55;
  color: var(--home-muted);
}

.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-decoration: none;
  color: var(--home-bg);
  background: var(--home-ink);
  transition: transform 0.35s ease, background 0.35s ease;
}

.cta:hover {
  transform: translateY(-2px);
  background: #fff;
}

.hero-collage {
  position: relative;
  height: min(62vh, 540px);
}

.hero-collage figure {
  position: absolute;
  margin: 0;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid rgba(246, 243, 238, 0.18);
  box-shadow: 0 22px 50px rgba(8, 6, 12, 0.45);
  background: var(--home-glass);
}

.hero-collage img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.c0 {
  width: 44%;
  aspect-ratio: 3 / 4;
  left: 4%;
  top: 10%;
  transform: rotate(-8deg);
  z-index: 1;
}

.c1 {
  width: 50%;
  aspect-ratio: 3 / 4;
  right: 5%;
  top: 2%;
  transform: rotate(4deg);
  z-index: 2;
}

.c2 {
  width: 38%;
  aspect-ratio: 5 / 4;
  left: 18%;
  bottom: 4%;
  transform: rotate(12deg);
  z-index: 3;
}

.season-badge {
  position: absolute;
  top: 2%;
  right: 2%;
  z-index: 4;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--home-ink);
  background: var(--home-violet);
  box-shadow: 0 8px 24px rgba(166, 126, 255, 0.4);
}

.recolor {
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.22fr);
  gap: clamp(22px, 4vw, 56px);
  align-items: center;
  margin: 8px 0 72px;
  text-align: left;
}

.recolor-copy h2 {
  margin: 0 0 12px;
  max-width: 12ch;
  font-size: clamp(1.7rem, 3.4vw, 2.6rem);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 1.05;
}

.recolor-copy p:not(.kicker-chip) {
  margin: 0 0 12px;
  max-width: 38ch;
  font-size: 1rem;
  line-height: 1.55;
  color: var(--home-muted);
}

.recolor-copy .cta {
  margin-top: 10px;
}

.recolor-pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.recolor-pair figure {
  position: relative;
  margin: 0;
  overflow: hidden;
  border-radius: 22px;
  border: 1px solid var(--home-line);
  aspect-ratio: 16 / 10;
  background: var(--home-glass);
  transition: transform 0.4s ease, border-color 0.4s ease;
}

.recolor-pair figure:hover {
  transform: translateY(-4px);
  border-color: rgba(246, 243, 238, 0.32);
}

.recolor-pair figure.tall {
  aspect-ratio: 4 / 5;
}

.recolor-pair img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
  transition: transform 0.7s ease;
}

.recolor-pair figure:hover img {
  transform: scale(1.04);
}

.recolor-pair figure.tall img {
  object-position: center;
}

.recolor-pair figcaption {
  position: absolute;
  left: 10px;
  bottom: 10px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--home-ink);
  background: var(--home-glass-strong);
  backdrop-filter: blur(10px);
}

@media (max-width: 900px) {
  .hero,
  .recolor,
  .recolor-pair {
    grid-template-columns: 1fr;
  }

  .hero {
    min-height: 0;
    gap: 20px;
    padding-top: 12px;
  }

  .hero-collage {
    height: 420px;
  }
}

@media (max-width: 640px) {
  .hero-collage {
    height: 340px;
  }

  .c0,
  .c1,
  .c2 {
    width: 52%;
  }
}

@keyframes blob-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  50% {
    transform: translate(6%, 8%) scale(1.08);
  }
}

@keyframes rise-in {
  from {
    opacity: 0;
    transform: translateY(22px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .blob,
  .hero-copy .kicker-chip,
  .hero-copy h1,
  .hero-copy .lead,
  .hero-copy .cta,
  .cta,
  .blobs,
  .recolor-pair figure,
  .recolor-pair img {
    animation: none;
    transition: none;
  }

  .c0,
  .c1,
  .c2 {
    transform: none;
  }

  .recolor-pair figure:hover,
  .recolor-pair figure:hover img {
    transform: none;
  }
}
</style>
<style lang="scss">
@import './home-stage.scss';
</style>
